<script setup>
import { computed, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  isPhoneE164OrEmpty,
  hasNoSpacesOrSpecialCharacters,
} from 'shared/helpers/Validators';

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const state = reactive({
  inboxName: '',
  phoneNumber: '',
});

const rules = {
  inboxName: {
    required: helpers.withMessage(
      () => t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR'),
      required
    ),
    format: helpers.withMessage(
      () => t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.FORMAT_ERROR'),
      value => hasNoSpacesOrSpecialCharacters(value)
    ),
  },
  phoneNumber: { required, isPhoneE164OrEmpty },
};

const v$ = useVuelidate(rules, state);

const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const inboxNameError = computed(
  () => v$.value.inboxName.$errors[0]?.$message || ''
);

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    const sessionName = (state.inboxName || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, '_');

    const whatsappChannel = await store.dispatch('inboxes/createChannel', {
      name: state.inboxName?.trim(),
      channel: {
        type: 'whatsapp',
        phone_number: state.phoneNumber,
        provider: 'waha',
        provider_config: {
          session_name: sessionName,
        },
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: whatsappChannel.id,
      },
    });
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel">
    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="state.inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ inboxNameError }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.phoneNumber.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
        <input
          v-model="state.phoneNumber"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
        </span>
      </label>
    </div>

    <div class="w-full">
      <NextButton
        type="submit"
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
        :is-loading="uiFlags.isCreating"
      />
    </div>
  </form>
</template>
